import { format, getMonth, getYear } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { cn } from '@shared/utils/utils';
import { Button } from '../button/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

export interface MonthPickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  id?: string;
  error?: boolean;
  fromYear?: number;
  toYear?: number;
  /** Meses posteriores a este mes quedan deshabilitados (p. ej. no permitir futuro). */
  toDate?: Date;
}

const MONTH_INDICES = Array.from({ length: 12 }, (_, index) => index);

function capitalizeMonth(label: string) {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function MonthPicker({
  date,
  onDateChange,
  placeholder = 'Seleccionar mes',
  disabled = false,
  className,
  'data-testid': testId,
  id,
  error = false,
  fromYear = 2018,
  toYear = new Date().getFullYear() + 1,
  toDate,
}: MonthPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [viewYear, setViewYear] = React.useState(() => (date ? getYear(date) : getYear(new Date())));

  const maxSelectable = React.useMemo(() => {
    if (!toDate) return null;
    return new Date(getYear(toDate), getMonth(toDate), 1);
  }, [toDate]);

  const effectiveToYear = maxSelectable ? Math.min(toYear, getYear(maxSelectable)) : toYear;

  React.useEffect(() => {
    if (date) {
      setViewYear(getYear(date));
    }
  }, [date]);

  const isMonthDisabled = (monthIndex: number) => {
    if (viewYear < fromYear || viewYear > effectiveToYear) return true;
    if (!maxSelectable) return false;
    return new Date(viewYear, monthIndex, 1) > maxSelectable;
  };

  const handleMonthSelect = (monthIndex: number) => {
    if (isMonthDisabled(monthIndex)) return;
    onDateChange?.(new Date(viewYear, monthIndex, 1));
    setOpen(false);
  };

  const canGoPrev = viewYear > fromYear;
  const canGoNext = viewYear < effectiveToYear;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal h-10 px-3 py-2 bg-card-bg border-card-border transition-colors',
            !date && 'text-muted-foreground',
            error && 'border-destructive focus-visible:ring-destructive',
            className,
          )}
          disabled={disabled}
          data-testid={testId}
          id={id}
          type="button"
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {date ? capitalizeMonth(format(date, 'MMMM yyyy', { locale: es })) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="mb-3 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!canGoPrev}
            onClick={() => setViewYear((year) => year - 1)}
            aria-label="Año anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[4rem] text-center text-sm font-semibold text-foreground">{viewYear}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!canGoNext}
            onClick={() => setViewYear((year) => year + 1)}
            aria-label="Año siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {MONTH_INDICES.map((monthIndex) => {
            const monthLabel = capitalizeMonth(format(new Date(viewYear, monthIndex, 1), 'MMM', { locale: es }));
            const isSelected = !!date && getYear(date) === viewYear && getMonth(date) === monthIndex;
            const monthDisabled = isMonthDisabled(monthIndex);

            return (
              <Button
                key={monthIndex}
                type="button"
                variant={isSelected ? 'default' : 'ghost'}
                size="sm"
                disabled={monthDisabled}
                className={cn(
                  'h-9 px-2 text-sm font-normal',
                  !isSelected && !monthDisabled && 'text-foreground hover:bg-muted',
                  monthDisabled && 'opacity-40 text-muted-foreground cursor-not-allowed',
                )}
                onClick={() => handleMonthSelect(monthIndex)}
              >
                {monthLabel}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

MonthPicker.displayName = 'MonthPicker';

export { MonthPicker };
